import FundInvestmentInvestor from '../../../models/admin/FundInvestmentData';
import BackendService from '../../../services/admin/BackendService';
import { getInvestmentPromptPrefill } from './investorPromotionUtils';

export type InvestorOperationRunner = (
  run: (opts: { overrideFundStatusRestriction?: boolean }) => Promise<void>,
  onSuccess: () => void,
  relaxUiWhileAwaitingAck?: boolean,
) => void;

export function runPromoteInvestor(
  investor: FundInvestmentInvestor,
  handleWithPrerequisites: InvestorOperationRunner,
  onRefresh: () => void,
  openInvestmentPrompt: (patch: {
    investmentPromptStage: FundInvestmentInvestor['status'];
    investmentPromptInvestor: FundInvestmentInvestor;
    investmentPromptAmount: string;
  }) => void,
  clearInProgress: () => void,
): void {
  const currentStatus = investor.status;

  if (currentStatus === 'CANDIDATE') {
    const service = new BackendService();
    handleWithPrerequisites(
      (opts) => service.promoteToLead(investor.id, opts),
      () => {
        clearInProgress();
        onRefresh();
      },
    );
    return;
  }

  if (currentStatus === 'LEAD') {
    openInvestmentPrompt({
      investmentPromptStage: 'OPPORTUNITY',
      investmentPromptInvestor: investor,
      investmentPromptAmount: investor.eoiSubmitted ? getInvestmentPromptPrefill(investor) : '',
    });
    return;
  }

  if (currentStatus === 'OPPORTUNITY') {
    openInvestmentPrompt({
      investmentPromptStage: 'COMMITTED_INVESTOR',
      investmentPromptInvestor: investor,
      investmentPromptAmount: getInvestmentPromptPrefill(investor),
    });
    return;
  }

  clearInProgress();
}

export function runDemoteInvestor(
  investor: FundInvestmentInvestor,
  handleWithPrerequisites: InvestorOperationRunner,
  onRefresh: () => void,
  clearInProgress: () => void,
): void {
  const service = new BackendService();
  const onDemoteDone = () => {
    clearInProgress();
    onRefresh();
  };

  if (investor.status === 'LEAD' && investor.eoiSubmitted) {
    handleWithPrerequisites((opts) => service.withdrawEOI(investor.id, opts), onDemoteDone);
  } else if (investor.status === 'LEAD') {
    handleWithPrerequisites((opts) => service.demoteToCandidate(investor.id, opts), onDemoteDone);
  } else if (investor.status === 'OPPORTUNITY') {
    handleWithPrerequisites((opts) => service.demoteToLead(investor.id, opts), onDemoteDone);
  } else if (investor.status === 'COMMITTED_INVESTOR') {
    handleWithPrerequisites((opts) => service.demoteToOpportunity(investor.id, opts), onDemoteDone);
  } else {
    clearInProgress();
  }
}

export function runDeleteInvestor(
  investor: FundInvestmentInvestor,
  onRefresh: () => void,
  clearInProgress: () => void,
): void {
  const service = new BackendService();
  service
    .deleteInvestor(investor.id)
    .then(() => {
      clearInProgress();
      onRefresh();
    })
    .catch(() => {
      clearInProgress();
    });
}

export function runAddAllCandidates(
  fundOpportunityId: string,
  onRefresh: () => void,
  setAddAllInProgress: (inProgress: boolean) => void,
): void {
  setAddAllInProgress(true);
  const service = new BackendService();
  service
    .addAllCandidates(fundOpportunityId)
    .then(() => {
      setAddAllInProgress(false);
      onRefresh();
    })
    .catch(() => {
      setAddAllInProgress(false);
    });
}

export function runSubmitInvestmentPrompt(
  investor: FundInvestmentInvestor,
  stage: FundInvestmentInvestor['status'],
  amountText: string | undefined,
  handleWithPrerequisites: InvestorOperationRunner,
  onSuccess: () => void,
  onInvalidAmount: () => void,
  onUnsupported: (message: string) => void,
): void {
  const amount = parseFloat(amountText || '0');
  if (amount <= 0) {
    onInvalidAmount();
    return;
  }

  const service = new BackendService();

  if (investor.status === 'LEAD' && !investor.eoiSubmitted) {
    handleWithPrerequisites(
      (opts) => service.submitEOI(investor.id, amount, opts),
      onSuccess,
      true,
    );
  } else if (investor.status === 'LEAD' && investor.eoiSubmitted && stage === 'OPPORTUNITY') {
    handleWithPrerequisites(
      (opts) => service.promoteToOpportunity(investor.id, amount, opts),
      onSuccess,
      true,
    );
  } else if (investor.status === 'OPPORTUNITY' && stage === 'COMMITTED_INVESTOR') {
    handleWithPrerequisites(
      (opts) => service.commitInvestment(investor.id, amount, opts),
      onSuccess,
      true,
    );
  } else {
    onUnsupported(`Unsupported operation: ${investor.status} -> ${stage}`);
  }
}
